import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table, Spinner, Modal, Button, Form } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './deploymentHistory.scss';
import BreadCrumbs from '../../components/BreadCrumbs/BreadCrumbs';
import { useAuth } from '../../Contexts/AuthContext';

interface DeploymentPkgData {
  deployment_pkg_id: number;
  deployment_transaction_id: string;
  deployment_pkg_name: string;
  src_environment_id?: number;
  src_environment_name?: string;
  tgt_environment_id?: number;
  tgt_environment_name?: string;
  is_rollbacked?: boolean;
  rolledback_at?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  customer?: string;
  created_at?: string;
  deployed_at?: string;
  status: string;
}

interface EnvironmentShort {
  environment_id: number;
  customer_environment_name: string;
}

interface UserShort {
  user_id: number;
  first_name: string;
}

const statusMapping = (status: number | null) => {
  switch (status) {
    case 1:
      return 'NEW';
    case 2:
      return 'UPDATED';
    case 3:
      return 'DELETED';
    case 0:
      return 'NO_CHANGE';
    default:
      return 'inherit';
  }
};

const formatDeployedAt = (item: DeploymentPkgData) => {
  const status = String(item.status || '').toUpperCase();
  if (status === 'FAILED' || status === 'INITIALIZED' || !item.deployed_at) {
    return '--';
  }

  const deployedAt = new Date(item.deployed_at);
  return Number.isNaN(deployedAt.getTime()) ? '--' : deployedAt.toLocaleString();
};

type DeploymentPkgInfoData = Record<string, any>;

const DeploymentHistory: React.FC = () => {
  const [data, setData] = useState<DeploymentPkgData[]>([]);
  const [selectedPkgId, setSelectedPkgId] = useState<number | null>(null);
  const [pkgInfoData, setPkgInfoData] = useState<DeploymentPkgInfoData[]>([]);
  const [loading, setLoading] = useState(false);
  const [infoLoading, setInfoLoading] = useState(false);
  const [error, setError] = useState({ Error: '' });
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showInfoErrorModal, setShowInfoErrorModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentInfoPage, setCurrentInfoPage] = useState(1);
  const [lastSuccessfulPage, setLastSuccessfulPage] = useState(1);
  const [lastSuccessfulInfoPage, setLastSuccessfulInfoPage] = useState(1);
  const pageSize = 100;

  const [environments, setEnvironments] = useState<EnvironmentShort[]>([]);
  const [users, setUsers] = useState<UserShort[]>([]);

  const [selectedEnvironment, setSelectedEnvironment] = useState<EnvironmentShort | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserShort | null>(null);
  const [tempEnvironmentId, setTempEnvironmentId] = useState<number | undefined>(undefined);
  const [tempUserId, setTempUserId] = useState<number | undefined>(undefined);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const { tokens } = useAuth();

  const statusOptions = useMemo(
    () => Array.from(new Set(data.map((item) => item.status).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [data]
  );
  const filteredData = useMemo(
    () => (selectedStatus ? data.filter((item) => item.status === selectedStatus) : data),
    [data, selectedStatus]
  );
  const sortedData = useMemo(
    () => [...filteredData].sort((a, b) => b.deployment_pkg_id - a.deployment_pkg_id),
    [filteredData]
  );

  const importDeploymentBreadCrumbData: { name: string; url: string }[] = [
    { name: 'Deployment', url: '/metaDataDeployment' },
    { name: 'Deployment History', url: '/deploymentHistory' },
  ];

  useEffect(() => {
    const env = environments.find((e) => e.environment_id === tempEnvironmentId) || null;
    const user = users.find((u) => u.user_id === tempUserId) || null;

    setSelectedEnvironment(env);
    setSelectedUser(user);
  }, [tempEnvironmentId, tempUserId, environments, users]);

  useEffect(() => {
    if (!tokens.accessToken) {
      return;
    }
    const fetchEnvironmentAndUsers = async () => {
      try {
        const customer_id = localStorage.getItem('customer_id');
        const url = `${process.env.REACT_APP_API_URL}deployment_pkg_history/filter_option_data?customer_id=${customer_id}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch environment-user summary');
        }

        const result = await response.json();

        setEnvironments(result.environments || []);
        setUsers(result.users || []);
      } catch (fetchError: any) {
        console.error('Error fetching environment-user summary:', fetchError);
      }
    };

    fetchEnvironmentAndUsers();
  }, [tokens.accessToken]);

  const prevStartDateRef = useRef<string | null>(null);
  const prevEndDateRef = useRef<string | null>(null);

  useEffect(() => {
    const startDateChanged = startDate !== prevStartDateRef.current;
    const endDateChanged = endDate !== prevEndDateRef.current;
    const ranDueToDateChange = startDateChanged || endDateChanged;

    prevStartDateRef.current = startDate;
    prevEndDateRef.current = endDate;

    if (ranDueToDateChange) {
      if (!startDate || !endDate) {
        return;
      }
    }

    const fetchData = async () => {
      if (!tokens.accessToken) {
        setLoading(false);
        setInfoLoading(false);
        setError({ Error: 'Missing access token. Please sign in again.' });
        setShowErrorModal(true);
        return;
      }
      try {
        setLoading(true);
        setInfoLoading(true);
        setError({ Error: '' });
        const customer_id = localStorage.getItem('customer_id');
        let url = `${process.env.REACT_APP_API_URL}deployment_pkg/history?page=${currentPage}&page_size=${pageSize}&customer_id=${customer_id}`;

        if (selectedUser?.user_id != null) {
          url += `&user_id=${selectedUser.user_id}`;
        }
        if (selectedEnvironment?.environment_id != null) {
          url += `&target_environment_id=${selectedEnvironment.environment_id}`;
        }
        if (startDate && endDate) {
          url += `&from_date=${encodeURIComponent(startDate + 'T00:00:00')}`;
          url += `&to_date=${encodeURIComponent(endDate + 'T23:59:59')}`;
        }
        if (selectedStatus) {
          url += `&status=${encodeURIComponent(selectedStatus)}`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        });
        if (!response.ok) {
          let errorMessage = 'Failed to fetch data';
          try {
            const errorBody = await response.json();
            if (typeof errorBody === 'string') {
              errorMessage = errorBody;
            } else if (typeof errorBody === 'object') {
              if (typeof errorBody.detail === 'string') {
                errorMessage = errorBody.detail;
              } else if (Array.isArray(errorBody.detail)) {
                errorMessage = errorBody.detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join(', ');
              } else if (typeof errorBody.message === 'string') {
                errorMessage = errorBody.message;
              } else {
                errorMessage = JSON.stringify(errorBody);
              }
            }
          } catch {
            errorMessage = response.statusText || 'An error occurred';
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();

        if (result?.deployment_package_history?.length === 0) {
          throw new Error('No data found for this page.');
        }

        setData(result?.deployment_package_history?.items || result?.deployment_package_history);
        if (result?.total_pages) {
          setLastSuccessfulPage(currentPage);
        }

        if (result?.deployment_package_history?.length > 0) {
          setSelectedPkgId(result?.deployment_package_history[0].deployment_pkg_id);
        }
      } catch (requestError) {
        let errorMessage = 'Unknown error occurred';
        if (requestError instanceof Error) {
          errorMessage = requestError.message;
        } else if (typeof requestError === 'string') {
          errorMessage = requestError;
        }
        console.error('Error fetching data:', errorMessage);
        setError({ Error: errorMessage });
        setShowErrorModal(true);
      } finally {
        setLoading(false);
        setInfoLoading(false);
      }
    };

    fetchData();
  }, [currentPage, selectedEnvironment, selectedUser, startDate, endDate, selectedStatus, tokens.accessToken]);

  useEffect(() => {
    const fetchPkgInfoData = async () => {
      if (selectedPkgId) {
        if (!tokens.accessToken) {
          setPkgInfoData([]);
          setInfoLoading(false);
          return;
        }
        try {
          setInfoLoading(true);

          const customer_id = localStorage.getItem('customer_id');

          const response = await fetch(
            `${process.env.REACT_APP_API_URL}deployment_pkg/history/info?page=${currentInfoPage}&page_size=${pageSize}&deployment_pkg_id=${selectedPkgId}&customer_id=${customer_id}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${tokens.accessToken}`,
              },
            }
          );

          if (!response.ok) {
            let errorMessage = 'Failed to fetch data';
            try {
              const errorBody = await response.json();
              if (typeof errorBody === 'string') {
                errorMessage = errorBody;
              } else if (typeof errorBody === 'object') {
                if (typeof errorBody.detail === 'string') {
                  errorMessage = errorBody.detail;
                } else if (Array.isArray(errorBody.detail)) {
                  errorMessage = errorBody.detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join(', ');
                } else if (typeof errorBody.message === 'string') {
                  errorMessage = errorBody.message;
                } else {
                  errorMessage = JSON.stringify(errorBody);
                }
              }
            } catch {
              errorMessage = response.statusText || 'An error occurred';
            }
            throw new Error(errorMessage);
          }

          const result = await response.json();
          setPkgInfoData(result?.deployment_package_history);
          if (result?.page_size) {
            setLastSuccessfulInfoPage(currentInfoPage);
          }
        } catch (requestError) {
          let errorMessage = 'Unknown error occurred';
          if (requestError instanceof Error) {
            errorMessage = requestError.message;
          } else if (typeof requestError === 'string') {
            errorMessage = requestError;
          }
          console.error('Error fetching data:', errorMessage);
          setError({ Error: errorMessage });
          setShowInfoErrorModal(true);
          setPkgInfoData([]);
        } finally {
          setInfoLoading(false);
        }
      }
    };

    fetchPkgInfoData();
  }, [selectedPkgId, currentInfoPage, tokens.accessToken]);

  return (
    <>
      <div className="deployment-history-page__crumbs">
        <BreadCrumbs breadCrumbsData={importDeploymentBreadCrumbData} />
      </div>

      <div className="deployment-history-page">
        <div className="deployment-history-topbar">
          <div className="deployment-history-topbar__titleWrap">
            <span className="deployment-history-topbar__titleIcon" aria-hidden="true">
              📦
            </span>
            <h1 className="deployment-history-title">Deployment Packages</h1>
          </div>
          <Button
            className="deployment-history-reset"
            variant="outline-primary"
            onClick={() => {
              setTempEnvironmentId(undefined);
              setTempUserId(undefined);
              setSelectedEnvironment(null);
              setSelectedUser(null);
              setStartDate(null);
              setEndDate(null);
              setSelectedStatus('');
              setCurrentPage(1);
              setCurrentInfoPage(1);
            }}
          >
            Reset Filters
          </Button>
        </div>

        <div className="deployment-history-filterbar">
          <Form className="deployment-history-filters">
            <Form.Group className="deployment-filter-group">
              <Form.Label className="deployment-filter-label">Select Environment</Form.Label>
              <Form.Select
                className="deployment-filter-select"
                value={tempEnvironmentId ?? ''}
                onChange={(e) => setTempEnvironmentId(e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">-- Choose Environment --</option>
                {environments.map((env) => (
                  <option key={env.environment_id} value={env.environment_id}>
                    {env.customer_environment_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="deployment-filter-group">
              <Form.Label className="deployment-filter-label">Select User</Form.Label>
              <Form.Select
                className="deployment-filter-select"
                value={tempUserId ?? ''}
                onChange={(e) => setTempUserId(e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">-- Choose User --</option>
                {users.map((user) => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.first_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="deployment-filter-group deployment-filter-group--date">
              <Form.Label className="deployment-filter-label">Start Date</Form.Label>
              <Form.Control
                className="deployment-filter-input"
                type="date"
                value={startDate ?? ''}
                onChange={(e) => setStartDate(e.target.value || null)}
              />
            </Form.Group>

            <Form.Group className="deployment-filter-group deployment-filter-group--date">
              <Form.Label className="deployment-filter-label">End Date</Form.Label>
              <Form.Control
                className="deployment-filter-input"
                type="date"
                value={endDate ?? ''}
                onChange={(e) => setEndDate(e.target.value || null)}
              />
            </Form.Group>

            <Form.Group className="deployment-filter-group">
              <Form.Label className="deployment-filter-label">Select Status</Form.Label>
              <Form.Select
                className="deployment-filter-select"
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">-- Choose Status --</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </div>

        <div className="deployment-history-panel">
          <div className="deployment-history-panelHeader">
            <div className="deployment-history-panelHeader__left">
              <span className="deployment-history-panelHeader__icon" aria-hidden="true">
                📋
              </span>
              <h2 className="deployment-history-subtitle">Deployment Packages</h2>
            </div>
          </div>
          <div className="deployment-history-table-wrap custom-scroll">
            {!loading ? (
              <Table bordered hover variant="light" className="deployment-history-table table-pretty fixed-width-table sticky-header">
                <thead className="table-dark">
                  <tr>
                    <th>Deployment Package ID</th>
                    <th>Deployed At</th>
                    <th>Status</th>
                    <th>Source Environment</th>
                    <th>Target Environment</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Customer</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((item) => (
                    <tr
                      key={item.deployment_pkg_id}
                      className={selectedPkgId === item.deployment_pkg_id ? 'deployment-history-row--active' : ''}
                      onClick={() => {
                        if (selectedPkgId !== item.deployment_pkg_id) {
                          setInfoLoading(true);
                          setSelectedPkgId(item.deployment_pkg_id);
                        }
                      }}
                    >
                      <td>{item.deployment_pkg_id}</td>
                      <td>{formatDeployedAt(item)}</td>
                      <td>
                        <span className={`history-status-pill history-status-pill--${String(item.status || '').toLowerCase()}`}>{item.status}</span>
                      </td>
                      <td>{item.src_environment_name || 'N/A'}</td>
                      <td>{item.tgt_environment_name || 'N/A'}</td>
                      <td>{item.first_name || 'N/A'}</td>
                      <td>{item.last_name || 'N/A'}</td>
                      <td>{item.email || 'N/A'}</td>
                      <td>{item.customer || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="deployment-history-loader">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            )}
          </div>
          <div className="deployment-history-footer">
            <span className="deployment-history-footer__count">
              Showing 1 to {sortedData.length} of {sortedData.length} results
            </span>
          </div>
        </div>

        <div className="deployment-history-panel deployment-history-panel--secondary">
          <div className="deployment-history-panelHeader">
            <div className="deployment-history-panelHeader__left">
              <span className="deployment-history-panelHeader__icon" aria-hidden="true">
                📋
              </span>
              <h2 className="deployment-history-subtitle">Deployment Package Info</h2>
            </div>
          </div>
          <div className="deployment-history-table-wrap custom-scroll">
            {!infoLoading ? (
              pkgInfoData.length > 0 ? (
                <Table bordered hover variant="light" className="deployment-history-table table-pretty fixed-width-table sticky-header">
                  <thead className="table-dark">
                    <tr>
                      <th>Deployment Package ID</th>
                      <th>Job Name</th>
                      <th>Job Status</th>
                      <th>Job Task Group Name</th>
                      <th>Job Task Group Status</th>
                      <th>Job Task Name</th>
                      <th>Job Task Status</th>
                      <th>Job Step Name</th>
                      <th>Job Step Status</th>
                      <th>Medallion Layer</th>
                      <th>Source Entity Name</th>
                      <th>Source Entity Status</th>
                      <th>Source Entity Schema</th>
                      <th>Data Source Name</th>
                      <th>Data Source Status</th>
                      <th>Target Entity Name</th>
                      <th>Target Entity Status</th>
                      <th>Target Entity Type</th>
                      <th>Custom Plugin Name</th>
                      <th>Custom Plugin Status</th>
                      <th>Custom Plugin Type</th>
                      <th>Data Mart</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pkgInfoData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.deployment_pkg_id}</td>
                        <td>{item.job_name ?? 'N/A'}</td>
                        <td>
                          <span className={`history-status-pill history-status-pill--${String(statusMapping(item.job_status)).toLowerCase()}`}>
                            {item.job_status != null ? statusMapping(item.job_status) : 'N/A'}
                          </span>
                        </td>
                        <td>{item.job_task_group_name ?? 'N/A'}</td>
                        <td>
                          <span className={`history-status-pill history-status-pill--${String(statusMapping(item.job_task_group_status)).toLowerCase()}`}>
                            {item.job_task_group_status != null ? statusMapping(item.job_task_group_status) : 'N/A'}
                          </span>
                        </td>
                        <td>{item.job_task_name ?? 'N/A'}</td>
                        <td>{item.job_task_status != null ? statusMapping(item.job_task_status) : 'N/A'}</td>
                        <td>{item.job_step_name ?? 'N/A'}</td>
                        <td>{item.job_step_status != null ? statusMapping(item.job_step_status) : 'N/A'}</td>
                        <td>{item.layer ?? 'N/A'}</td>
                        <td>{item.source_entity_name ?? 'N/A'}</td>
                        <td>{item.source_entity_status != null ? statusMapping(item.source_entity_status) : 'N/A'}</td>
                        <td>{item.source_entity_schema ?? 'N/A'}</td>
                        <td>{item.data_source_name ?? 'N/A'}</td>
                        <td>{item.data_source_status != null ? statusMapping(item.data_source_status) : 'N/A'}</td>
                        <td>{item.target_entity_name ?? 'N/A'}</td>
                        <td>{item.target_entity_status != null ? statusMapping(item.target_entity_status) : 'N/A'}</td>
                        <td>{item.entity_type ?? 'N/A'}</td>
                        <td>{item.custom_plugin_name ?? 'N/A'}</td>
                        <td>{item.custom_plugin_status != null ? statusMapping(item.custom_plugin_status) : 'N/A'}</td>
                        <td>{item.custom_plugin_type ?? 'N/A'}</td>
                        <td>{item.data_mart ?? 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="deployment-history-empty">The Error Occurred While Fetching</div>
              )
            ) : (
              <div className="deployment-history-loader">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            )}
          </div>
          <div className="deployment-history-footer">
            <span className="deployment-history-footer__count">
              Showing 1 to {pkgInfoData.length} of {pkgInfoData.length} results
            </span>
          </div>
        </div>
      </div>

      <Modal
        show={showErrorModal}
        onHide={() => {
          setShowErrorModal(false);
          setCurrentPage(lastSuccessfulPage);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Something Went Wrong</Modal.Title>
        </Modal.Header>
        <Modal.Body>{error?.Error}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowErrorModal(false);
              setCurrentPage(lastSuccessfulPage);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showInfoErrorModal}
        onHide={() => {
          setShowInfoErrorModal(false);
          setCurrentInfoPage(lastSuccessfulInfoPage);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Something Went Wrong</Modal.Title>
        </Modal.Header>
        <Modal.Body>{error?.Error}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowInfoErrorModal(false);
              setCurrentInfoPage(lastSuccessfulPage);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DeploymentHistory;
